'use client';
import { useId, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { cn } from 'lib/utils';
import {
  type BloomProps,
  Bloom,
  type HeaderSectionProps,
  HeaderSection,
  Icon,
  Wrapper,
} from 'ui';
import { type IconNames } from 'atoms/Icon/Icons';

export type IconTextGridProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  headerAlignment?: HeaderSectionProps['alignment'];
  items: {
    title: string;
    icon: IconNames;
  }[];
  bloom?: BloomProps;
};

const IconTextGrid = ({
  className,
  eyebrow,
  title,
  intro,
  headerAlignment,
  items,
  bloom,
}: IconTextGridProps) => {
  const componentId = useId();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  // Do not render if there are fewer than 3 items
  if (!items?.length || items.length < 3) return null;

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        variant="page-full"
        className={cn('relative flex flex-col gap-14', className)}
      >
        <Bloom {...bloom} />

        <HeaderSection
          content={intro}
          alignment={headerAlignment}
          {...{ eyebrow, title }}
          className="relative z-2"
        />

        <div
          className="relative z-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-20 md:gap-x-20 gap-x-30"
          role="list"
          aria-label={`${items.length} icons and descriptions displayed`}
        >
          {items.slice(0, 9).map((item, index) => {
            return (
              <motion.article
                key={index}
                role="listitem"
                className="flex flex-col gap-4"
                aria-labelledby={`${componentId}-${index}-title`}
                initial={{ opacity: 0, y: 30 }}
                animate={
                  isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                }
                transition={{
                  duration: 0.6,
                  delay: 0.1 + index * 0.1,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div className="size-6 flex" aria-hidden="true">
                  <Icon name={item.icon} className="size-auto fill-stone-100" />
                </div>

                <p className="text-p1" id={`${componentId}-${index}-title`}>
                  {item.title}
                </p>
              </motion.article>
            );
          })}
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default IconTextGrid;
